/**
 * Version Upload Hook
 *
 * Custom hook to handle file version uploads with:
 * - File validation (type and size) via shared utilities
 * - Upload progress tracking
 * - Query cache invalidation
 * - Error handling
 *
 * IMPORTANT: Storage limit checking is NOT handled by this hook.
 * Consumers must use useVersionWithStorageLimit to check storage
 * BEFORE calling uploadFile, and render the StorageLimitDialog.
 *
 * @module features/workspace/hooks/useVersionUpload
 */

import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import type { VersionCreateResponse, UploadResult, ApiErrorResponse } from '@loader/shared-types';

import { toast } from '@/lib/toast';
import { compressFile } from '@/lib/compression';
import { API_BASE_URL, MAX_FILE_SIZE } from '@/config/constants';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateVersionQueries } from '@/lib/queryInvalidation';
import {
  validateFileType,
  formatFileSize,
  extractErrorMessage,
  ALLOWED_FILE_EXTENSIONS,
} from '@/lib/fileUtils';
import { isValidUUID } from '@/utils/fieldIdHelpers';

/**
 * Hook return type
 */
export interface UseVersionUploadResult {
  /**
   * Upload a file to create a new version
   *
   * @param file - File to upload
   * @param tableId - Table ID to create version for
   * @param options - Optional upload options
   * @param options.sheetName - Excel sheet name to import (null if no sheet selected)
   * @param options.baseVersionId - Version ID to append to (enables append mode)
   * @returns Upload result with versionId if successful, null otherwise
   */
  uploadFile: (
    file: File,
    tableId: string,
    options?: { sheetName?: string | null; baseVersionId?: string | null }
  ) => Promise<UploadResult | null>;

  /**
   * Whether a file is currently being uploaded
   */
  isUploading: boolean;

  /**
   * Upload progress percentage (0-100)
   */
  uploadProgress: number;

  /**
   * Error message if upload failed
   */
  error: string | null;

  /**
   * Reset error state
   */
  resetError: () => void;

  /**
   * Reset progress to 0 (call before starting a new upload)
   */
  resetProgress: () => void;
}

/**
 * Hook to handle version file uploads
 *
 * Features:
 * - File validation (type and size) via shared utilities
 * - Upload progress tracking
 * - Automatic query invalidation on success
 * - Error handling with user-friendly messages
 *
 * NOTE: Storage capacity checking must be done by the consumer
 * using useVersionWithStorageLimit before calling uploadFile.
 *
 * @returns Upload utilities and state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { uploadFile, isUploading, uploadProgress, error } = useVersionUpload();
 *
 *   const handleUpload = async (file: File, tableId: string) => {
 *     const result = await uploadFile(file, tableId);
 *     if (result) {
 *       console.log('Version created:', result.versionId);
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       {isUploading && <Progress value={uploadProgress} />}
 *       {error && <ErrorMessage>{error}</ErrorMessage>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useVersionUpload(): UseVersionUploadResult {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Track the current XHR to abort if a new upload starts
  const currentXhrRef = useRef<XMLHttpRequest | null>(null);

  /**
   * Reset error state
   */
  const resetError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Reset progress to 0 (call before starting a new upload to avoid stale progress display)
   */
  const resetProgress = useCallback(() => {
    setUploadProgress(0);
  }, []);

  /**
   * Upload file to create new version
   */
  const uploadFile = async (
    file: File,
    tableId: string,
    options?: { sheetName?: string | null; baseVersionId?: string | null }
  ): Promise<UploadResult | null> => {
    const { sheetName, baseVersionId } = options ?? {};
    // Abort any in-flight upload before starting a new one
    if (currentXhrRef.current) {
      currentXhrRef.current.abort();
      currentXhrRef.current = null;
    }

    // Reset state
    setError(null);
    setUploadProgress(0);

    // Validate file type using shared utility
    if (!validateFileType(file)) {
      const errorMsg = `Invalid file type. Allowed types: ${ALLOWED_FILE_EXTENSIONS.join(', ')}`;
      setError(errorMsg);
      toast.version.validationError(errorMsg);
      return null;
    }

    // Validate file is not empty
    if (file.size === 0) {
      const errorMsg = 'File is empty';
      setError(errorMsg);
      toast.version.validationError(errorMsg);
      return null;
    }

    // NOTE: Storage capacity checking should be done by the consumer
    // using useVersionWithStorageLimit BEFORE calling this function.
    // The backend will also validate storage limits as a safety net.

    setIsUploading(true);

    try {
      // Compress CSV/JSON files before upload (5-10x smaller transfer)
      const { blob, fileName, compressed } = await compressFile(file);

      // Validate size after compression — compressed payload is what hits the CF Worker
      if (blob.size > MAX_FILE_SIZE) {
        const errorMsg = `File too large${compressed ? ' (even after compression)' : ''}: ${formatFileSize(blob.size)}. Maximum allowed: ${formatFileSize(MAX_FILE_SIZE)}`;
        setError(errorMsg);
        toast.version.validationError(errorMsg);
        setIsUploading(false);
        return null;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', new File([blob], fileName, { type: blob.type }));

      // Add sheet name if provided (for Excel workbooks with multiple sheets)
      // The backend will use this to extract data from the specific sheet
      if (sheetName) {
        formData.append('sheet_name', sheetName);
      }

      // Send original file metadata so backend uses it for validation and records
      if (compressed) {
        formData.append('original_file_name', file.name);
        formData.append('original_file_size', String(file.size));
      }

      // Get authentication token
      const token = await getToken();

      // Create XMLHttpRequest for progress tracking
      // Note: fetch() doesn't support upload progress, so we use XMLHttpRequest
      const xhr = new XMLHttpRequest();
      currentXhrRef.current = xhr;

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        }
      });

      // Create promise to handle XHR completion with proper typing
      const uploadPromise = new Promise<VersionCreateResponse>((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText) as VersionCreateResponse;
              resolve(response);
            } catch {
              reject(new Error('Failed to parse response'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText) as ApiErrorResponse;
              reject(new Error(extractErrorMessage(errorData, 'Failed to process version')));
            } catch {
              reject(new Error('Failed to process version'));
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });
      });

      // Configure and send request
      // Build URL with query parameters (validate UUIDs before constructing URL)
      if (!isValidUUID(tableId)) {
        throw new Error('Invalid table ID');
      }
      if (baseVersionId && !isValidUUID(baseVersionId)) {
        throw new Error('Invalid base version ID');
      }
      const url = new URL(`${API_BASE_URL}/api/private/uploads/process`);
      url.searchParams.set('table_id', tableId);
      if (baseVersionId) {
        url.searchParams.set('mode', 'append');
        url.searchParams.set('base_version_id', baseVersionId);
      }
      xhr.open('POST', url.toString());

      // Set authorization header if token exists
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      // Send request
      xhr.send(formData);

      // Wait for upload to complete
      const data = await uploadPromise;

      // Clear the XHR reference now that upload is complete
      currentXhrRef.current = null;

      // Validate response
      if (!data.version?.id) {
        throw new Error('Version response missing version ID');
      }

      // Invalidate version queries
      await invalidateVersionQueries(queryClient, tableId);
      // Also invalidate operations panel
      await queryClient.invalidateQueries({
        queryKey: queryKeys.tables.operations.recent(tableId),
      });

      // Check if validation failed (version created but marked as failed)
      if (data.success === false) {
        const errorMessage = data.message || 'Validation failed';
        setError(errorMessage);
        toast.version.processingFailed(errorMessage);
        setIsUploading(false);
        setUploadProgress(0);
        // Return versionId so UI can show the failed version
        return { versionId: data.version.id, tableId };
      }

      // Show success toast
      toast.version.created();

      // Reset upload state
      setIsUploading(false);
      setUploadProgress(100);

      return { versionId: data.version.id, tableId };
    } catch (err) {
      // Clear the XHR reference on error
      currentXhrRef.current = null;

      // Don't show error for aborted uploads (user started a new upload)
      if (err instanceof Error && err.message === 'Upload cancelled') {
        setIsUploading(false);
        setUploadProgress(0);
        return null;
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to upload file';
      setError(errorMessage);
      toast.version.processingFailed(errorMessage);
      setIsUploading(false);
      setUploadProgress(0);
      return null;
    }
  };

  return {
    uploadFile,
    isUploading,
    uploadProgress,
    error,
    resetError,
    resetProgress,
  };
}
